import { Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  analytics,
  getPageInfo,
  getPageName,
  type PortalApp,
} from "@/analytics";
import { useHostServices } from "@/hooks/useHostServices";
import { Breadcrumbs, Loader, Tabs } from "@/ui/components";

interface SubAppLayoutProps {
  appName: PortalApp;
  children: React.ReactNode;
}

const SubAppLayout = ({ appName, children }: SubAppLayoutProps) => {
  const { pathname } = useLocation();
  const {
    breadcrumbs,
    setBreadcrumbs,
    tabs,
    setTabs,
    showPrintButton,
    setShowPrintButton,
  } = useHostServices() || {};

  // Outlet reuses this layout whenever the next route also renders
  // SubAppLayout, so unmount cleanup never runs between board / honorarium /
  // tax-forms. Re-running on pathname drops chrome the previous page set;
  // the new page's child fills it in from its own effect.
  useEffect(() => {
    return () => {
      if (setBreadcrumbs) setBreadcrumbs(undefined);
      if (setShowPrintButton) setShowPrintButton(false);
      if (setTabs) setTabs(undefined);
    };
  }, [pathname, setBreadcrumbs, setShowPrintButton, setTabs]);

  // Chrome-level clicks are tracked here, once, rather than in every page
  // that sets breadcrumbs or tabs.
  const page = getPageName(pathname);
  // Crumb labels and hrefs can carry personal data (a conferee's name in the
  // label, their name in the hash), so neither is sent: the item is the
  // destination's page name and the destination is its route template.
  const onBreadcrumbNavigate = (_label: string, href: string) => {
    const { page: item, path_template } = getPageInfo(href.split(/[?#]/)[0]);
    analytics.track("Navigation Clicked", {
      item,
      destination: path_template,
      location: "breadcrumb",
    });
  };
  const onPrint = () => analytics.track("Print Clicked", { page });
  const onTabSelect = (tab: string) =>
    analytics.track("Tab Selected", { app: appName, page, tab });

  return (
    <div className="flex flex-col min-h-[calc(50vh-56px)] lg:min-h-screen justify-stretch overflow-visible">
      <header
        className={`sticky top-14 lg:top-0 z-20 overflow-clip print:hidden ${!breadcrumbs ? "hidden" : ""}`}
      >
        <Breadcrumbs
          pages={breadcrumbs ?? []}
          showPrintButton={showPrintButton}
          onNavigate={onBreadcrumbNavigate}
          onPrint={onPrint}
        />
        <div
          className={`sticky hidden sm:flex top-[45px] z-10 px-2 bg-gray-100 ${!tabs ? "hidden" : ""}`}
        >
          <Tabs tabs={tabs ?? []} onTabSelect={onTabSelect} />
        </div>
      </header>

      <main className="w-full flex-1">
        <Suspense
          fallback={
            <div className="py-10 self-center">
              <Loader text={`${appName} Page`} />
            </div>
          }
        >
          {children}
        </Suspense>
      </main>

      <footer
        className={`sm:hidden sticky bottom-0 z-10 px-2 bg-gray-100 print:hidden ${!tabs ? "hidden" : ""}`}
      >
        <Tabs tabs={tabs ?? []} onTabSelect={onTabSelect} />
      </footer>
    </div>
  );
};

export default SubAppLayout;
